import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Text,
  Avatar,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { searchCustomers } from 'services/unifiedCustomersSearch';

const debounce = (fn, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), wait);
  };
};

// Selector simple de alumno por nombre/RUT, devuelve { id, username, name, group }
const StudentPicker = ({ value, onSelect, placeholder = 'Buscar alumno por nombre o RUT…' }) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const popoverBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const runSearch = useCallback(debounce(async (q) => {
    if (!q || q.trim().length < 1) {
      setResults([]);
      setSearching(false);
      return;
    }
    try {
      const res = await searchCustomers(q, 8);
      setResults(res);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, 300), []);

  useEffect(() => {
    if (term.trim()) {
      setSearching(true);
      runSearch(term);
      setOpen(true);
    } else {
      setResults([]);
      setOpen(false);
    }
  }, [term, runSearch]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handlePick = (user) => {
    // meta_data se pasa para que GuardianPicker (Salida por Enfermedad) pueda
    // ofrecer el apoderado registrado del alumno sin una llamada aparte.
    onSelect({ id: user.id, username: user.username, name: user.name, group: user.group, meta_data: user.meta_data });
    setTerm('');
    setResults([]);
    setOpen(false);
  };

  return (
    <Box position="relative" ref={boxRef}>
      {value ? (
        <HStack
          p={2}
          borderRadius="8px"
          border="1px solid"
          borderColor={borderColor}
          justify="space-between"
        >
          <HStack spacing={2} minW={0}>
            <Avatar size="xs" name={value.name} />
            <VStack align="flex-start" spacing={0} minW={0}>
              <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{value.name}</Text>
              <Text fontSize="xs" color={mutedColor}>RUT: {value.username}{value.group ? ` · ${value.group}` : ''}</Text>
            </VStack>
          </HStack>
          <Text
            fontSize="xs"
            color="primary.500"
            cursor="pointer"
            fontWeight="semibold"
            onClick={() => onSelect(null)}
          >
            Cambiar
          </Text>
        </HStack>
      ) : (
        <InputGroup size="sm">
          <InputLeftElement>
            <SearchIcon color="gray.400" boxSize={3} />
          </InputLeftElement>
          <Input
            borderRadius="8px"
            placeholder={placeholder}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onFocus={() => term.trim() && setOpen(true)}
          />
        </InputGroup>
      )}

      {open && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          zIndex={20}
          bg={popoverBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="8px"
          boxShadow="md"
          maxH="240px"
          overflowY="auto"
        >
          {searching ? (
            <HStack p={3} justify="center">
              <Spinner size="sm" color="primary.500" />
            </HStack>
          ) : results.length === 0 ? (
            <Text fontSize="sm" color={mutedColor} p={3} textAlign="center">
              Sin resultados
            </Text>
          ) : (
            <VStack spacing={0} align="stretch">
              {results.map((u) => (
                <HStack
                  key={u.id}
                  p={2}
                  spacing={2}
                  cursor="pointer"
                  _hover={{ bg: hoverBg }}
                  onClick={() => handlePick(u)}
                >
                  <Avatar size="xs" name={u.name} />
                  <VStack align="flex-start" spacing={0} minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{u.name}</Text>
                    <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                      RUT: {u.username}{u.group ? ` · ${u.group}` : ''}
                    </Text>
                  </VStack>
                </HStack>
              ))}
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StudentPicker;
